import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { reloadPage } from '../features/version/core/reloadPage';
import { toText } from '../utils/text';

/*
 * Catches errors thrown while rendering a page — most importantly a
 * page chunk that can no longer be fetched.
 *
 * Pages are loaded lazily (see plugins/registry.tsx), and their file
 * names carry a content hash. After a deployment the previous
 * release's chunks are gone from the server; a long-open tab that
 * navigates into a not-yet-loaded area then requests a file that no
 * longer exists. Our SPA rewrite answers such requests with
 * index.html — status 200, content type text/html — so the dynamic
 * import does not get a 404 but HTML where JavaScript was expected,
 * and fails with a syntax error. Reloading fixes it, because
 * index.html is served uncached and points at the current chunks.
 *
 * Any other render error is a programming error, not a stale tab, so
 * it gets the generic message instead of a misleading "new version"
 * claim.
 */

/* the wording differs per browser, hence the broad set of markers */
const CHUNK_ERROR_MARKERS = [
  'loading chunk',
  'loading css chunk',
  'dynamically imported module',
  'failed to fetch dynamically imported',
  'importing a module script failed',
  "unexpected token '<'",
  'expected expression, got',
];

type ThrownError = { name?: string; message?: string };

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const thrown = error as ThrownError;
  if (thrown.name === 'ChunkLoadError') return true;

  const message = toText(thrown.message || error).toLowerCase();
  return CHUNK_ERROR_MARKERS.some((marker) => message.includes(marker));
}

function PageErrorMessage({ error }: { error: ThrownError }) {
  const { t } = useTranslation();
  const stale = isChunkLoadError(error);

  return (
    <div className="error-notice" role="alert">
      <p className="error-notice-message">
        {stale ? t('version.staleChunk') : t('error.generic')}
      </p>
      <button className="error-notice-retry" onClick={reloadPage}>
        {t('version.reload')}
      </button>
      {error?.message && (
        <details className="error-notice-details">
          <summary>{t('error.details')}</summary>
          <p>{error.message}</p>
        </details>
      )}
    </div>
  );
}

type ErrorBoundaryState = { error: ThrownError | null };

class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: ThrownError): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    /* the console is where support asks users to look */
    console.error('page failed to render', error);
  }

  render() {
    if (this.state.error) return <PageErrorMessage error={this.state.error} />;
    return this.props.children;
  }
}

/*
 * React never resets an error boundary by itself — without the key the
 * user would stay stuck on the message even after navigating
 * elsewhere. Keying on the path remounts the boundary per page, which
 * is what routing does with the page below it anyway.
 */
export function PageErrorBoundary({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}
